import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { AdminHeader } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DataImport from "./DataImport";
import { toast } from "sonner";
import { ExternalLink, ArrowLeft, Upload, Trash2, Copy } from "lucide-react";

const ALL_MODULES = ["dashboard", "customers", "sites", "contracts", "assets", "jobs", "schedule", "defects", "quotations", "stock", "purchasing", "projects", "people", "vehicles", "billing", "documents", "reports", "settings"];

export default function CompanyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get(`/admin/companies/${id}`).then((r) => { setC(r.data); setForm(r.data); });
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    const { users, user_count, ...body } = form;
    await api.put(`/admin/companies/${id}`, body); toast.success("Company updated"); load();
  };
  const toggle = (m) => setForm((f) => ({ ...f, modules: (f.modules || []).includes(m) ? f.modules.filter((x) => x !== m) : [...(f.modules || []), m] }));
  const openWs = () => { localStorage.setItem("active_company_id", id); nav("/app/dashboard"); };

  const logoRef = useRef(null);
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/companies/${id}/logo`, fd); toast.success("Logo uploaded"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  const removeLogo = async () => {
    try { await api.delete(`/companies/${id}/logo`); toast.success("Logo removed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const loginUrl = c?.slug ? `${window.location.origin}/login/${c.slug}` : "";

  if (!c) return <div className="min-h-screen bg-slate-50"><AdminHeader /><div className="p-6 text-slate-400">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminHeader />
      <div className="p-6">
        <button onClick={() => nav("/admin/companies")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-3 transition-colors"><ArrowLeft className="h-4 w-4" />Companies</button>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md grid place-items-center overflow-hidden" style={{ backgroundColor: c.logo ? "#fff" : c.accent_color }}>
              {c.logo ? <img src={`${API}/public/companies/${c.id}/logo?v=${c.logo_version || 0}`} alt={c.name} className="h-full w-full object-contain p-0.5" /> : <span className="text-white font-head font-bold text-lg">{c.name[0]}</span>}
            </div>
            <div><h1 className="text-2xl font-head font-bold text-slate-900">{c.name}</h1><p className="text-sm text-slate-500">{c.user_count} users · {(c.modules || []).length} modules · {c.plan}</p></div>
          </div>
          <Button className="btn-accent" data-testid="detail-open-ws" onClick={openWs}>Open workspace <ExternalLink className="h-4 w-4 ml-1" /></Button>
        </div>

        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details" data-testid="detail-tab-details">Details & Subscription</TabsTrigger>
            <TabsTrigger value="modules" data-testid="detail-tab-modules">Modules</TabsTrigger>
            <TabsTrigger value="users" data-testid="detail-tab-users">Users</TabsTrigger>
            <TabsTrigger value="import" data-testid="detail-tab-import">Data Import</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-md p-5 max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
              {[["name", "Company name"], ["trading_name", "Trading name"], ["address", "Address"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"], ["renewal_date", "Renewal date"], ["user_limit", "User limit"]].map(([k, l]) => (
                <div key={k}><Label className="text-xs text-slate-600">{l}</Label><Input data-testid={`detail-${k}`} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 bg-white" /></div>
              ))}
              <div><Label className="text-xs text-slate-600">Subscription plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}><SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{["Starter", "Professional", "Enterprise"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-600">Account status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}><SelectTrigger data-testid="detail-status" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{["active", "suspended", "archived"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-600">Brand accent colour</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" data-testid="detail-accent" value={form.accent_color || "#FFA502"} onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))} className="h-9 w-14 rounded border border-slate-200" />
                  <Input value={form.accent_color || ""} onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))} className="bg-white" />
                </div></div>
              <div><Label className="text-xs text-slate-600">Company logo</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  {c.logo ? (
                    <img data-testid="detail-logo-preview" src={`${API}/public/companies/${c.id}/logo?v=${c.logo_version || 0}`} alt="Company logo" className="h-10 max-w-[130px] object-contain border border-slate-200 rounded-md bg-white p-1" />
                  ) : (
                    <span className="text-xs text-slate-400">No logo</span>
                  )}
                  <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={uploadLogo} />
                  <Button type="button" size="sm" variant="outline" data-testid="detail-logo-upload" onClick={() => logoRef.current?.click()}><Upload className="h-4 w-4 mr-1" />Upload</Button>
                  {c.logo && <Button type="button" size="sm" variant="outline" className="text-red-600 hover:text-red-700" data-testid="detail-logo-remove" onClick={removeLogo}><Trash2 className="h-4 w-4" /></Button>}
                </div></div>
              {loginUrl && (
                <div className="md:col-span-2"><Label className="text-xs text-slate-600">Branded sign-in link</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input readOnly data-testid="detail-login-link" value={loginUrl} className="bg-slate-50 text-slate-600" />
                    <Button type="button" variant="outline" data-testid="detail-login-copy" onClick={() => { navigator.clipboard.writeText(loginUrl); toast.success("Sign-in link copied"); }}><Copy className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">This company's users see their own logo and colours when signing in here.</p>
                </div>
              )}
              <div className="md:col-span-2"><Button className="btn-accent" data-testid="detail-save" onClick={save}>Save changes</Button></div>
            </div>
          </TabsContent>

          <TabsContent value="modules" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-md p-5 max-w-2xl">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {ALL_MODULES.map((m) => (
                  <label key={m} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    <span className="text-sm capitalize text-slate-700">{m}</span>
                    <Switch data-testid={`detail-module-${m}`} disabled={m === "dashboard" || m === "settings"} checked={(form.modules || []).includes(m)} onCheckedChange={() => toggle(m)} />
                  </label>
                ))}
              </div>
              <Button className="btn-accent mt-4" data-testid="detail-modules-save" onClick={save}>Save modules</Button>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm"><thead className="bg-slate-50"><tr>{["Name", "Email", "Role"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2.5">{h}</th>)}</tr></thead>
                <tbody>{(c.users || []).map((u) => <tr key={u.id} className="border-t border-slate-100"><td className="px-3 py-2.5 font-medium text-slate-800">{u.name}</td><td className="px-3 py-2.5 text-slate-600">{u.email}</td><td className="px-3 py-2.5 text-slate-600">{u.role}</td></tr>)}</tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="import" className="mt-4">
            <DataImport companyId={id} companyName={c.name} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
